/**
 * Image Map Section
 * Handles hotspot interactions, sequential pulsing, keyboard navigation, and design mode helpers
 */

(function() {
  'use strict';

  class ImageMap {
    constructor(section) {
      this.section = section;
      this.hotspotMarkers = Array.from(section.querySelectorAll('.hotspot-marker'));
      this.hotspotImage = section.querySelector('[data-hotspot-image]');
      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.currentPulsingIndex = 0;
      this.pulseTimeout = null;
      this.resizeTimeout = null;
      this.currentAnimationFrame = null;
      
      this.init();
    }

    init() {
      this.startSequentialPulsing();
      this.setupResizeHandler();
      this.setupHoverAnimation();
      
      // Design mode coordinate helper
      if (window.Shopify && Shopify.designMode) {
        this.setupCoordinateHelper();
      }
    }

    /**
     * Shared ring pulse animation logic
     */
    animateRing(ring, cycleProgress) {
      // Apply ease-in-out to the progress
      const eased = cycleProgress < 0.5
        ? 2 * cycleProgress * cycleProgress
        : 1 - Math.pow(-2 * cycleProgress + 2, 2) / 2;
      
      // 0 -> 0.5: scale 1->2, opacity 1->0
      // 0.5 -> 1: scale 2->1, opacity 0->1
      let scale, opacity;
      if (eased < 0.5) {
        const t = eased * 2;
        scale = 1 + t;
        opacity = 1 - t;
      } else {
        const t = (eased - 0.5) * 2;
        scale = 2 - t;
        opacity = t;
      }
      
      if (ring) {
        ring.style.transform = `scale(${scale})`;
        ring.style.opacity = opacity;
      }
    }

    /**
     * Setup hover animation for desktop
     */
    setupHoverAnimation() {
      this.hotspotMarkers.forEach(marker => {
        const ring = marker.querySelector('.hotspot-marker__ring');
        let animationFrame = null;
        let startTime = null;
        
        const animate = (currentTime) => {
          if (!startTime) startTime = currentTime;
          const elapsed = currentTime - startTime;
          const duration = 1000;
          const cycleProgress = (elapsed % duration) / duration;
          
          this.animateRing(ring, cycleProgress);
          animationFrame = requestAnimationFrame(animate);
        };
        
        marker.addEventListener('mouseenter', () => {
          // Only run on desktop
          if (window.innerWidth <= 767) return;
          
          startTime = null;
          if (animationFrame) cancelAnimationFrame(animationFrame);
          if (ring) ring.style.willChange = 'transform, opacity';
          animationFrame = requestAnimationFrame(animate);
        });
        
        marker.addEventListener('mouseleave', () => {
          // Only run on desktop
          if (window.innerWidth <= 767) return;
          
          if (animationFrame) {
            cancelAnimationFrame(animationFrame);
            animationFrame = null;
          }
          
          // Get current values
          const currentTransform = ring ? getComputedStyle(ring).transform : 'none';
          const currentOpacity = ring ? getComputedStyle(ring).opacity : '1';
          
          if (ring) {
            ring.style.transform = currentTransform;
            ring.style.opacity = currentOpacity;
          }
          
          void marker.offsetWidth;
          
          // Faster exit animation
          const exitDuration = 250;
          const exitStartTime = performance.now();
          
          const animateExit = (currentTime) => {
            const elapsed = currentTime - exitStartTime;
            const progress = Math.min(elapsed / exitDuration, 1);
            
            if (ring) {
              const startScale = parseFloat(currentTransform.match(/matrix\(([^,]+)/)?.[1] || '1');
              const startOpacity = parseFloat(currentOpacity);
              const scale = startScale - (startScale - 1) * progress;
              const opacity = startOpacity + (1 - startOpacity) * progress;
              ring.style.transform = `scale(${scale})`;
              ring.style.opacity = opacity;
            }
            
            if (progress < 1) {
              requestAnimationFrame(animateExit);
            } else {
              if (ring) {
                ring.style.transform = '';
                ring.style.opacity = '';
                ring.style.willChange = '';
              }
            }
          };
          
          requestAnimationFrame(animateExit);
        });
      });
    }

    /**
     * Sequential hotspot pulsing animation on mobile
     */
    startSequentialPulsing() {
      // Only run on mobile devices and if reduced motion is not preferred
      if (window.innerWidth > 767 || this.prefersReducedMotion) {
        return;
      }
      
      if (this.hotspotMarkers.length === 0) {
        return;
      }
      
      this.pulseNext();
    }

    pulseNext() {
      const currentMarker = this.hotspotMarkers[this.currentPulsingIndex];
      if (currentMarker) {
        const ring = currentMarker.querySelector('.hotspot-marker__ring');
        let startTime = null;
        const pulseDuration = 1200; // 1s per pulse
        const totalDuration = 2400; // 2 pulses
        
        const animate = (currentTime) => {
          if (!startTime) startTime = currentTime;
          const elapsed = currentTime - startTime;
          
          if (elapsed >= totalDuration) {
            // Animation complete, clean up
            if (ring) {
              ring.style.transform = '';
              ring.style.opacity = '';
              ring.style.willChange = '';
            }
            if (this.currentAnimationFrame) {
              cancelAnimationFrame(this.currentAnimationFrame);
              this.currentAnimationFrame = null;
            }
            
            // Move to next hotspot and start it
            this.currentPulsingIndex = (this.currentPulsingIndex + 1) % this.hotspotMarkers.length;
            this.pulseNext();
            return;
          }
          
          const cycleProgress = (elapsed % pulseDuration) / pulseDuration;
          this.animateRing(ring, cycleProgress);
          this.currentAnimationFrame = requestAnimationFrame(animate);
        };
        
        if (ring) ring.style.willChange = 'transform, opacity';
        this.currentAnimationFrame = requestAnimationFrame(animate);
      }
    }

    /**
     * Handle window resize events
     */
    setupResizeHandler() {
      window.addEventListener('resize', () => {
        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = setTimeout(() => {
          if (window.innerWidth <= 767 && !this.prefersReducedMotion) {
            this.startSequentialPulsing();
          } else {
            // Stop mobile animations on desktop
            if (this.currentAnimationFrame) {
              cancelAnimationFrame(this.currentAnimationFrame);
              this.currentAnimationFrame = null;
            }
            // Clean up all markers
            this.hotspotMarkers.forEach(marker => {
              const ring = marker.querySelector('.hotspot-marker__ring');
              if (ring) {
                ring.style.transform = '';
                ring.style.opacity = '';
                ring.style.willChange = '';
              }
            });
          }
        }, 500);
      });
    }

    /**
     * Design mode coordinate helper - click image to get coordinates
     */
    setupCoordinateHelper() {
      if (!this.hotspotImage) {
        return;
      }

      const coordinatesMessage = 'Copy these into your hotspot block settings.';
      
      // Remove existing listener if any
      if (this.coordinateClickHandler) {
        this.hotspotImage.removeEventListener('click', this.coordinateClickHandler);
      }

      // Create tooltip element if it doesn't exist
      let tooltip = this.section.querySelector('.coordinate-tooltip');
      if (!tooltip) {
        tooltip = document.createElement('div');
        tooltip.className = 'coordinate-tooltip';
        this.section.appendChild(tooltip);
      }
      this.coordinateTooltip = tooltip;

      // Create new click handler
      this.coordinateClickHandler = (e) => {
        // Stop event from bubbling to hotspots
        e.stopPropagation();
        e.preventDefault();
        
        const rect = this.hotspotImage.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width * 100).toFixed(1);
        const y = ((e.clientY - rect.top) / rect.height * 100).toFixed(1);

        // Show tooltip with coordinates
        this.coordinateTooltip.innerHTML = `
          <div class="coordinate-tooltip__content">
            <strong>Hotspot Coordinates:</strong><br>
            X: ${x}%<br>
            Y: ${y}%<br>
            <small>${coordinatesMessage}</small>
          </div>
        `;
        
        // Position tooltip - centered horizontally, vertically based on click
        this.coordinateTooltip.style.top = `${e.clientY}px`;
        this.coordinateTooltip.classList.add('is-visible');

        // Auto-hide after 3 seconds
        clearTimeout(this.tooltipTimeout);
        this.tooltipTimeout = setTimeout(() => {
          this.coordinateTooltip.classList.remove('is-visible');
        }, 3000);
      };

      this.hotspotImage.addEventListener('click', this.coordinateClickHandler);
      
      // Add visual indicator that image is clickable in design mode
      this.hotspotImage.style.cursor = 'crosshair';
      
      // Click anywhere to dismiss tooltip
      this.tooltipDismissHandler = (e) => {
        if (!this.coordinateTooltip.contains(e.target) && e.target !== this.hotspotImage) {
          this.coordinateTooltip.classList.remove('is-visible');
        }
      };
      document.addEventListener('click', this.tooltipDismissHandler);
    }

    /**
     * Reinitialize the section (used when blocks are added/removed in theme editor)
     */
    reinit() {
      // Clear existing timeouts
      clearTimeout(this.pulseTimeout);
      clearTimeout(this.resizeTimeout);
      
      // Re-query elements
      this.hotspotMarkers = Array.from(this.section.querySelectorAll('.hotspot-marker'));
      this.hotspotImage = this.section.querySelector('[data-hotspot-image]');
      
      // Reset pulsing index
      this.currentPulsingIndex = 0;
      
      // Remove all pulsing classes
      this.hotspotMarkers.forEach(marker => marker.classList.remove('is-pulsing'));
      
      // Reinitialize
      this.init();
    }

    /**
     * Clean up when section is removed
     */
    destroy() {
      clearTimeout(this.pulseTimeout);
      clearTimeout(this.resizeTimeout);
      clearTimeout(this.tooltipTimeout);
      
      // Remove coordinate helper
      if (this.coordinateClickHandler && this.hotspotImage) {
        this.hotspotImage.removeEventListener('click', this.coordinateClickHandler);
        this.hotspotImage.style.cursor = '';
      }
      
      // Remove tooltip dismiss handler
      if (this.tooltipDismissHandler) {
        document.removeEventListener('click', this.tooltipDismissHandler);
      }
      
      // Remove tooltip element
      if (this.coordinateTooltip) {
        this.coordinateTooltip.remove();
      }
      
      // Remove pulsing classes
      this.hotspotMarkers.forEach(marker => marker.classList.remove('is-pulsing'));
    }
  }

  // Store instances by section ID
  const imageMapInstances = new Map();

  /**
   * Initialize all image map sections on the page
   */
  function initImageMaps() {
    const sections = document.querySelectorAll('.image-map');
    
    sections.forEach(section => {
      const sectionId = section.id;
      
      // Skip if already initialized
      if (imageMapInstances.has(sectionId)) {
        return;
      }
      
      const instance = new ImageMap(section);
      imageMapInstances.set(sectionId, instance);
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      setTimeout(initImageMaps, 100);
    });
  } else {
    setTimeout(initImageMaps, 100);
  }

  // Theme editor support - handle section load
  document.addEventListener('shopify:section:load', function(event) {
    const section = event.target.querySelector('.image-map');
    
    if (section) {
      const sectionId = section.id;
      
      // Destroy existing instance if any
      if (imageMapInstances.has(sectionId)) {
        imageMapInstances.get(sectionId).destroy();
        imageMapInstances.delete(sectionId);
      }
      
      // Create new instance with a small delay to ensure DOM is ready
      setTimeout(() => {
        const instance = new ImageMap(section);
        imageMapInstances.set(sectionId, instance);
      }, 100);
    }
  });

  // Theme editor support - handle block changes (add/remove/reorder)
  document.addEventListener('shopify:block:select', function(event) {
    const section = event.target.closest('.image-map');
    
    if (section) {
      const sectionId = section.id;
      const instance = imageMapInstances.get(sectionId);
      
      if (instance) {
        // Reinitialize to pick up new blocks
        setTimeout(() => {
          instance.reinit();
        }, 100);
      }
    }
  });

  // Theme editor support - handle section unload
  document.addEventListener('shopify:section:unload', function(event) {
    const section = event.target.querySelector('.image-map');
    
    if (section) {
      const sectionId = section.id;
      const instance = imageMapInstances.get(sectionId);
      
      if (instance) {
        instance.destroy();
        imageMapInstances.delete(sectionId);
      }
    }
  });

})();
